/**
 * reservationHelpers.ts
 *
 * 予約データ操作のヘルパー関数
 */

/**
 * リソースIDを解決
 */
export const resolveResourceId = (res: Record<string, any> | null | undefined): string => {
    if (!res) return '';
    const id = res.id ?? res.resource_id ?? res.resourceId ?? res.vehicleId ?? null;
    return id != null ? String(id) : '';
};

/**
 * 車両IDを抽出
 */
export const extractVehicleId = (res: Record<string, any> | null | undefined, fallback: string = ''): string => {
    if (!res) return fallback;
    const candidates = [
        res.vehicleId,
        res.vehicle_id,
        res.vehicle?.id,
        res.vehicle?.resource_id,
        res.id,
        res.resource_id,
        fallback,
    ];
    for (const value of candidates) {
        if (value == null) continue;
        const text = String(value).trim();
        if (text.length > 0) return text;
    }
    return '';
};

/**
 * リソースリストからIDでリソースを検索
 */
export const findResourceById = (list: Record<string, any>[], id: string | number | null | undefined): Record<string, any> | null => {
    if (!Array.isArray(list) || !id) return null;
    return list.find((res) => {
        const rid = res && (res.id ?? res.resource_id ?? res.resourceId ?? res.vehicleId);
        return rid != null && String(rid) === String(id);
    }) || null;
};

/**
 * 予約の名前を取得
 */
export const deriveReservationName = (res: Record<string, any> | null | undefined): string => {
    if (!res) return '';
    return res.customer_name || res.customerName || res.contact_name || res.contactName || '';
};

/**
 * 予約ステータスを正規化
 */
export const normalizeStatusCode = (value: string | number | null | undefined): string | null => {
    if (!value) return null;
    const text = String(value).trim().toLowerCase();
    return text || null;
};

/**
 * 予約が有効かどうか判定
 */
export const isValidReservation = (res: Record<string, any> | null | undefined): boolean => {
    if (!res || typeof res !== 'object') return false;
    if (!res.id) return false;
    const startDate = res.start_date || res.startDate;
    const endDate = res.end_date || res.endDate;
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime());
};

/**
 * 日付入力値をDateに変換
 */
export const parseDateInputValue = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const text = String(value).trim();
    if (!text) return null;

    // YYYY-MM-DD, YYYY/MM/DD, MM/DD, M/D 形式をサポート
    const patterns: RegExp[] = [
        /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/,
        /^(\d{1,2})[-/](\d{1,2})$/,
    ];

    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (!match) continue;

        if (match.length === 4) {
            // YYYY-MM-DD
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            const date = new Date(year, month, day);
            if (!Number.isNaN(date.getTime())) return date;
        } else if (match.length === 3) {
            // MM/DD (current year)
            const now = new Date();
            const month = parseInt(match[1], 10) - 1;
            const day = parseInt(match[2], 10);
            const date = new Date(now.getFullYear(), month, day);
            if (!Number.isNaN(date.getTime())) return date;
        }
    }

    // 直接パース
    const direct = new Date(text);
    return Number.isNaN(direct.getTime()) ? null : direct;
};

/**
 * Dateを入力フィールド用文字列に変換
 */
export const toDateInputValue = (date: Date | null | undefined): string => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * 自社サイト(LP)直販予約の contact_code 接頭辞。
 *
 * backend(Laravel `app/Models/Reservation.php` の generateContactCode())が発番する。
 * 那覇は `TR-`、宮古は `PI-`(2026-10-06 本番データで確認: 8月以降の宮古の自社予約 116 件がすべて PI-。
 * 当初予定の `PT-` は実際には使われていないが、念のため残す)。
 * OTA 経由の予約は各社の採番(`00002M4APC` 等)なのでここには含まれない。
 *
 * ⚠️ 発番側が増えたらここに追加する。判定を各所に散らさないこと。
 */
export const DIRECT_BOOKING_CODE_PREFIXES = ['TR-', 'PI-', 'PT-'] as const;

/**
 * 自社サイト直販の予約か(カレンダーの★アイコン表示等に使う)。
 * 那覇 `TR-` / 宮古 `PI-`（旧予定 `PT-`）を受け付ける。
 */
export const isDirectBookingCode = (contactCode: string | null | undefined): boolean => {
    if (!contactCode) return false;
    const code = String(contactCode);
    return DIRECT_BOOKING_CODE_PREFIXES.some((p) => code.startsWith(p));
};
